// The systems and infrastructure report: every system and piece of infrastructure in the organisation, what it costs and
// emits, the work it does or supports, how information moves between systems (by an automated interface or by people
// re-keying it), where work is already automated and where it could be. Built from the organisation model (costs and
// carbon) and the analysis graph (the work, and how it flows), so it can be tested without a page
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { EDGE_TYPE, getInsightsForNode, GraphData, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { SYSTEM_TYPES } from '../common/information-value';
import { contributionOf, FindingSeverity, isFrictionWork, matrix, total, ValueContribution, WorkforceMatrix } from '../workforce/workforce-model';

// how an action is done: by systems alone, by people working in a system, by people outside any system, or by no one
// the model shows
// external: done only by external organisations (customers, suppliers), who aren't staff, so it isn't the organisation's work
export type AutomationLevel = 'automated' | 'assisted' | 'manual' | 'external' | 'unassigned';

export const AUTOMATION_LABELS: Record<AutomationLevel, string> = {
    automated: 'Automated',
    assisted: 'People using systems',
    manual: 'Outside systems',
    external: 'Done by external roles',
    unassigned: 'No one assigned',
};

// work outside any system, as one end of an interface
export const OUTSIDE_SYSTEMS = 'outside-systems';
const OUTSIDE_SYSTEMS_NAME = 'Outside systems';

// an action whose items take no longer than this each, and that happens at least this often, is repetitive
const REPETITIVE_SECONDS = 30 * 60;
const REPETITIVE_ITEMS = 500;
// a system involved in more than this share of the work done in systems, across several models, is one much depends on
const DEPENDENCY_SHARE = 0.25;
const DEPENDENCY_MODELS = 3;

export const systemsOf = (a: AnalysisNode) => [...new Set([...(a.systems || []), ...(a.roles || []).flatMap(r => r.systemKeys || [])])].sort();

const NO_EXTERNAL = new Set<string>();

// the roles that are external organisations: customers, suppliers and the like, who aren't staff
export const externalRoleKeys = (organisationModel: GraphData) =>
    new Set(organisationModel.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE && n.type === NODE_TYPE.EXTERNAL_ORGANISATION).map(n => n._key));

// how an action is done: by staff with or without systems, by systems alone, or only by external roles, whose time
// isn't the organisation's (a customer using the webstore is their work, not staff's)
export const automationLevel = (a: AnalysisNode, external: Set<string> = NO_EXTERNAL): AutomationLevel => {
    const roles = a.roles || [];
    const staff = roles.filter(r => !external.has(r._key)).length > 0;
    const systems = systemsOf(a).length > 0;
    if (staff) return systems ? 'assisted' : 'manual';
    if (roles.length) return 'external';
    return systems ? 'automated' : 'unassigned';
};

// the staff's share of an action's time: its roles share it equally (as the workforce report shares it), and external
// roles' shares aren't the organisation's. Nothing for an action no role does
export const staffHours = (a: AnalysisNode, external: Set<string> = NO_EXTERNAL) => {
    const roles = a.roles || [];
    if (!roles.length) return 0;
    return (a.hours || 0) * roles.filter(r => !external.has(r._key)).length / roles.length;
};

// external roles' share of an action's time
export const externalHours = (a: AnalysisNode, external: Set<string> = NO_EXTERNAL) => {
    const roles = a.roles || [];
    if (!roles.length) return 0;
    return (a.hours || 0) * roles.filter(r => external.has(r._key)).length / roles.length;
};

export interface SystemSummary {
    key: string;
    name: string;
    aiAgent: boolean;
    ownCost: number;                    // its own annual cost (licences, support)
    infrastructureCost: number;         // its share of the infrastructure it runs on
    cost: number;
    carbon: number;                     // kg CO2e a year, its own and its share of its infrastructure's
    hours: number;                      // hours a year of work done with it
    automatedActions: number;           // actions it does alone
    assistedActions: number;            // actions people do with it
    automatedItems: number;             // items a year it handles alone
    frictionActions: number;            // actions involving it that are friction work: exception work, and work marked as not adding value
    frictionHours: number;             // their hours a year
    frictionCost: number;              // and cost
    models: string[];                   // names
    roles: number;                      // roles using it
    infrastructure: string[];           // names
    interfacesIn: number;
    interfacesOut: number;
    outputs: number;                    // outputs its work supports
    used: boolean;                      // any modelled work involves it
    contribution: ValueContribution;    // the value and revenue the work it's used on creates (in a scope, in the scope's models)
}

// an output, and how the work that produces it is done: every action on its path, back to where the work starts
export interface OutputSummary {
    key: string;
    name: string;
    model: string;
    items: number;                      // a year
    actions: Record<AutomationLevel, number>;   // on its path, by how they're done
    steps: number;
    automatedShare: number | null;      // of the steps someone or something does
    hours: number;                      // people's time a year on its path
    systems: string[];                  // involved on its path, most steps first
    manualHandoffs: number;             // places on its path where people move information into a system, or out of systems
}

export interface InfrastructureSummary {
    key: string;
    name: string;
    kind: 'cloud' | 'on-premise' | 'other';
    cost: number;
    carbon: number;
    systems: { key: string; name: string; used: boolean }[];
}

// information passing from one system (or from outside systems) to another
export interface SystemInterface {
    fromKey: string;
    from: string;
    toKey: string;
    to: string;
    items: number;                      // a year
    automatedItems: number;             // of those, picked up by the receiving system with no one involved
    // the time and cost, a year, of the steps where people take the rest in by hand (their share of those steps). Not all
    // of it is moving the information: the steps may do other work too, so it's the most an interface could save
    manualHours: number;
    manualCost: number;
    actions: string[];                  // the receiving actions, by name
    actionKeys: string[];
    models: string[];
}

// service: a step done by external roles, such as customers, made easier for them; it saves no staff time
export type OpportunityKind = 'integrate' | 'automate' | 'digitise' | 'service';

// somewhere automation could take work off people
export interface AutomationOpportunity {
    kind: OpportunityKind;
    title: string;
    detail: string;
    hours: number;                      // a year, of staff time in the steps involved: the most it could free
    cost: number;                       // about, a year, of those steps
    externalHours?: number;             // a service opportunity: external roles' time in the step, which it would make easier
    models: string[];
    actionKeys: string[];
}

export interface SystemsFinding {
    severity: FindingSeverity;
    title: string;
    detail: string;
    weight: number;
}

export interface ModelAutomation {
    key: string;
    name: string;
    actions: Record<AutomationLevel, number>;   // numbers of actions
    total: number;
}

export interface SystemsReport {
    scoped: boolean;                    // for some of the organisation's models rather than all of them
    systems: SystemSummary[];
    infrastructure: InfrastructureSummary[];
    interfaces: SystemInterface[];
    outputs: OutputSummary[];
    opportunities: AutomationOpportunity[];
    findings: SystemsFinding[];
    models: ModelAutomation[];
    interfaceMatrix: WorkforceMatrix;   // items a year from systems (rows) to systems (columns)
    totals: {
        systems: number;
        aiAgents: number;
        infrastructure: number;
        cost: number;                   // systems' own and infrastructure's, each counted once
        carbon: number;
        actions: Record<AutomationLevel, number>;
        hours: Record<AutomationLevel, number>;
        staffHours: number;             // staff time a year in the actions: external roles' time isn't counted
        externalHours: number;          // external roles' time a year in the actions
        automatedShare: number | null;  // of the actions staff or systems do
        interfaces: number;
        manualInterfaces: number;       // with any items moved by people
        interfaceItems: number;
        automatedInterfaceItems: number;
        manualInterfaceHours: number;
        manualInterfaceCost: number;
        opportunityHours: number;       // staff time in the steps any opportunity involves, each step counted once
        frictionActions: number;        // actions that are friction work, with their hours and cost a year
        frictionHours: number;
        frictionCost: number;
        frictionAutomated: number;     // of those actions, the ones systems do alone
        valueContribution: number;      // the value and revenue the work systems are used on creates, and its cost
        revenueContribution: number;
        workCost: number;
    };
}

export interface SystemsInput {
    organisationModel: GraphData;
    analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] };
    workspaceKey: string;
    modelNames: { [modelKey: string]: string };
    money: (value: number) => string;
    number: (value: number) => string;
    // the analysis is some of the organisation's models rather than all of them: only the systems involved in their
    // work, and the infrastructure those run on, are reported, and nothing is said about systems no work uses
    scoped?: boolean;
}

const sumMap = (values: { map?: { [key: string]: number } } | undefined) => Object.values(values?.map || {}).reduce((sum, v) => sum + (v || 0), 0);
const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;
const list = (names: string[], max = 3) => names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} and ${names.length - max} more`;

export const buildSystemsReport = (input: SystemsInput): SystemsReport => {
    const { organisationModel, analysis, workspaceKey, modelNames, money, number } = input;
    const insightsOf = (n: LinqsetNode) => getInsightsForNode(n, ORGANISATION_MODEL_KEY, workspaceKey);
    const allSystemNodes = organisationModel.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM);
    const allInfrastructureNodes = organisationModel.nodes.filter(n => n.superType === NODE_SUPERTYPE.INFRASTRUCTURE);
    const names = new Map<string, string>(organisationModel.nodes.map(n => [n._key, n.name || 'Unnamed']));
    names.set(OUTSIDE_SYSTEMS, OUTSIDE_SYSTEMS_NAME);
    const modelName = (key: string) => modelNames[key] || 'Unnamed model';
    const actions = analysis.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION);
    const actionsByKey = new Map(actions.map(a => [a._key, a]));
    // external roles' time isn't the organisation's work, nor its to save
    const external = externalRoleKeys(organisationModel);
    const levelOf = (a: AnalysisNode) => automationLevel(a, external);
    const staffTime = (a: AnalysisNode) => staffHours(a, external);

    // the work: how each action is done, and the systems involved
    const levels = { automated: 0, assisted: 0, manual: 0, external: 0, unassigned: 0 } as Record<AutomationLevel, number>;
    const levelHours = { automated: 0, assisted: 0, manual: 0, external: 0, unassigned: 0 } as Record<AutomationLevel, number>;
    const byModel = new Map<string, ModelAutomation>();
    const systemWork = new Map<string, { automated: number; assisted: number; items: number; models: Set<string>; roles: Set<string>; frictionActions: number; frictionHours: number; frictionCost: number }>();
    const work = (key: string) => systemWork.get(key) || systemWork.set(key, { automated: 0, assisted: 0, items: 0, models: new Set(), roles: new Set(), frictionActions: 0, frictionHours: 0, frictionCost: 0 }).get(key)!;
    actions.forEach(a => {
        const level = levelOf(a);
        levels[level]++;
        levelHours[level] += a.hours || 0;
        const m = byModel.get(a.modelKey) || byModel.set(a.modelKey, { key: a.modelKey, name: modelName(a.modelKey), actions: { automated: 0, assisted: 0, manual: 0, external: 0, unassigned: 0 }, total: 0 }).get(a.modelKey)!;
        m.actions[level]++;
        m.total++;
        systemsOf(a).forEach(s => {
            const w = work(s);
            if (level === 'automated') { w.automated++; w.items += a.items || 0; }
            else w.assisted++;
            if (isFrictionWork(a)) {
                w.frictionActions++;
                w.frictionHours += a.hours || 0;
                w.frictionCost += a.cost || 0;
            }
            w.models.add(a.modelKey);
        });
        (a.roles || []).forEach(r => (r.systemKeys || []).forEach(s => work(s).roles.add(r._key)));
    });

    // interfaces: information leaving one system's action for an action involving another system, or entering or
    // leaving systems altogether. Automated when the receiving action has no one doing it, or when the information is
    // typed as held in a system (a database, an application or a service) and passes between steps in systems: an
    // interface carries it, though people then work on it. Otherwise people move it (handed on as a file, on paper or by
    // word of mouth, or typed in), and the share of the receiving action's time the information accounts for is time
    // spent moving it
    const interfaces = new Map<string, Omit<SystemInterface, 'actionKeys'> & { actionKeys: Set<string>; modelKeys: Set<string> }>();
    const byInterface = (e: AnalysisEdge, from: AnalysisNode, to: AnalysisNode) =>
        e.informationType !== undefined && SYSTEM_TYPES.has(e.informationType) && systemsOf(from).length > 0 && systemsOf(to).length > 0;
    analysis.edges.forEach(e => {
        const from = actionsByKey.get(e.from), to = actionsByKey.get(e.to);
        if (!from || !to || from._key === to._key) return;
        const fromSystems = systemsOf(from), toSystems = systemsOf(to);
        const toLevel = levelOf(to);
        if (toLevel === 'unassigned' || levelOf(from) === 'unassigned') return;
        const sources = fromSystems.length ? fromSystems : [OUTSIDE_SYSTEMS];
        const targets = (toSystems.length ? toSystems : [OUTSIDE_SYSTEMS]).filter(t => !sources.includes(t));
        const pairs = sources.flatMap(s => targets.map(t => [s, t] as [string, string])).filter(([s, t]) => s !== t);
        if (!pairs.length) return;
        const items = e.items || 0;
        const automated = toLevel === 'automated' || byInterface(e, from, to);
        const share = to.items ? Math.min(1, items / to.items) : 1;
        pairs.forEach(([s, t]) => {
            const key = `${s}>${t}`;
            const i = interfaces.get(key) || interfaces.set(key, {
                fromKey: s, from: names.get(s) || 'Unnamed', toKey: t, to: names.get(t) || 'Unnamed', items: 0, automatedItems: 0, manualHours: 0, manualCost: 0,
                actions: [], models: [], actionKeys: new Set<string>(), modelKeys: new Set<string>(),
            }).get(key)!;
            i.items += items / pairs.length;
            if (automated) i.automatedItems += items / pairs.length;
            else {
                // the staff's time moving it; an external role re-keying it into our system costs us nothing
                const staffShare = to.hours ? staffTime(to) / to.hours : 0;
                i.manualHours += staffTime(to) * share / pairs.length;
                i.manualCost += (to.cost || 0) * staffShare * share / pairs.length;
            }
            i.actionKeys.add(to._key);
            i.modelKeys.add(to.modelKey);
        });
    });
    const interfaceList: SystemInterface[] = [...interfaces.values()].map(({ actionKeys, modelKeys, ...i }) => ({
        ...i, actions: [...actionKeys].map(k => actionsByKey.get(k)?.name || 'Unnamed action'), actionKeys: [...actionKeys], models: [...modelKeys].map(modelName),
    })).sort((a, b) => b.manualHours - a.manualHours || b.items - a.items);

    // the infrastructure each system runs on; in a scope, only the systems its work involves and their infrastructure
    const provides = organisationModel.edges.filter(e => e.type === EDGE_TYPE.PROVIDES);
    const systemNodes = input.scoped ? allSystemNodes.filter(n => systemWork.has(n._key)) : allSystemNodes;
    const systemKeys = new Set(systemNodes.map(s => s._key));
    const scopeModels = input.scoped ? new Set(actions.map(a => a.modelKey)) : undefined;
    const infrastructureNodes = input.scoped ? allInfrastructureNodes.filter(n => provides.some(e => e.sourceKey === n._key && systemKeys.has(e.targetKey))) : allInfrastructureNodes;

    const systems: SystemSummary[] = systemNodes.map(n => {
        const i = insightsOf(n);
        const w = systemWork.get(n._key);
        const ownCost = total(i?.costAllocation);
        const infrastructureCost = i?.allocatedCostsByType?.map?.[NODE_SUPERTYPE.INFRASTRUCTURE] || 0;
        return {
            key: n._key, name: names.get(n._key)!, aiAgent: n.type === NODE_TYPE.AI_AGENT, ownCost, infrastructureCost, cost: ownCost + infrastructureCost,
            carbon: sumMap(i?.allocatedCarbonByScope), hours: i?.hours || 0, automatedActions: w?.automated || 0, assistedActions: w?.assisted || 0,
            automatedItems: w?.items || 0, frictionActions: w?.frictionActions || 0, frictionHours: w?.frictionHours || 0, frictionCost: w?.frictionCost || 0,
            models: [...(w?.models || [])].map(modelName).sort(), roles: w?.roles.size || 0,
            infrastructure: provides.filter(e => e.targetKey === n._key).map(e => names.get(e.sourceKey) || 'Unnamed'),
            interfacesIn: interfaceList.filter(x => x.toKey === n._key).length, interfacesOut: interfaceList.filter(x => x.fromKey === n._key).length,
            outputs: i?.outputsSupported || 0, used: !!w,
            contribution: contributionOf(n, workspaceKey, scopeModels),
        };
    }).sort((a, b) => b.cost - a.cost || b.automatedActions + b.assistedActions - a.automatedActions - a.assistedActions);
    const systemByKey = new Map(systems.map(s => [s.key, s]));

    const infrastructure: InfrastructureSummary[] = infrastructureNodes.map(n => {
        const i = insightsOf(n);
        const hosted = provides.filter(e => e.sourceKey === n._key && systemKeys.has(e.targetKey))
            .map(e => ({ key: e.targetKey, name: names.get(e.targetKey) || 'Unnamed', used: !!systemByKey.get(e.targetKey)?.used }));
        return {
            key: n._key, name: names.get(n._key)!, kind: n.type === NODE_TYPE.CLOUD_BASED ? 'cloud' : n.type === NODE_TYPE.ON_PREMISE ? 'on-premise' : 'other',
            cost: total(i?.costAllocation), carbon: (i?.emissions || []).reduce((sum, e) => sum + total(e), 0), systems: hosted,
        } as InfrastructureSummary;
    }).sort((a, b) => b.cost - a.cost || b.carbon - a.carbon);

    // outputs: the actions on each one's path, found by walking back from the actions that create it
    const incoming = new Map<string, AnalysisEdge[]>();
    analysis.edges.forEach(e => (incoming.get(e.to) || incoming.set(e.to, []).get(e.to)!).push(e));
    const crossesSystems = (e: AnalysisEdge, from: AnalysisNode, to: AnalysisNode) => {
        const toLevel = levelOf(to);
        if (toLevel === 'automated' || toLevel === 'unassigned' || levelOf(from) === 'unassigned' || byInterface(e, from, to)) return false;
        const a = systemsOf(from).join('+'), b = systemsOf(to).join('+');
        return a !== b && (b !== '' || a !== '');
    };
    const outputs: OutputSummary[] = analysis.nodes.filter(n => n.superType === NODE_SUPERTYPE.INFORMATION).map(o => {
        const path = new Set<string>();
        const stack = (incoming.get(o._key) || []).map(e => e.from).filter(k => actionsByKey.has(k));
        while (stack.length) {
            const k = stack.pop()!;
            if (path.has(k)) continue;
            path.add(k);
            (incoming.get(k) || []).forEach(e => { if (actionsByKey.has(e.from) && !path.has(e.from)) stack.push(e.from); });
        }
        const steps = [...path].map(k => actionsByKey.get(k)!);
        const counts = { automated: 0, assisted: 0, manual: 0, external: 0, unassigned: 0 } as Record<AutomationLevel, number>;
        const bySystem = new Map<string, number>();
        steps.forEach(a => {
            counts[levelOf(a)]++;
            systemsOf(a).forEach(s => bySystem.set(s, (bySystem.get(s) || 0) + 1));
        });
        const done = counts.automated + counts.assisted + counts.manual;
        const manualHandoffs = steps.reduce((sum, to) => sum + (incoming.get(to._key) || [])
            .filter(e => path.has(e.from) && crossesSystems(e, actionsByKey.get(e.from)!, to)).length, 0);
        return {
            key: o._key, name: o.name || 'Unnamed output', model: modelName(o.modelKey), items: o.items || 0, actions: counts, steps: steps.length,
            automatedShare: done ? counts.automated / done : null, hours: steps.reduce((sum, a) => sum + staffTime(a), 0),
            systems: [...bySystem.entries()].map(([k, n]) => ({ name: names.get(k) || 'Unnamed', n })).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name)).map(s => s.name), manualHandoffs,
        };
    }).filter(o => o.steps > 0).sort((a, b) => b.hours - a.hours || b.items - a.items);

    // where automation could take work off people
    const opportunities: AutomationOpportunity[] = [];
    interfaceList.filter(i => i.manualHours > 0 && i.fromKey !== OUTSIDE_SYSTEMS && i.toKey !== OUTSIDE_SYSTEMS).forEach(i => opportunities.push({
        kind: 'integrate', title: `Connect ${i.from} to ${i.to}`, hours: i.manualHours, cost: i.manualCost, models: i.models, actionKeys: i.actionKeys,
        detail: `People take ${number(i.items - i.automatedItems)} items a year from ${i.from} into ${i.to} by hand, in ${list(i.actions)}. An interface would remove the re-keying from those steps.`,
    }));
    const staffNames = (a: AnalysisNode) => list((a.roles || []).filter(r => !external.has(r._key)).map(r => names.get(r._key) || 'Unnamed role'));
    actions.filter(a => levelOf(a) === 'manual' && staffTime(a) > 0).forEach(a => opportunities.push({
        kind: 'digitise', title: `Bring "${a.name || 'Unnamed action'}" into a system`, hours: staffTime(a), cost: (a.cost || 0) * staffTime(a) / (a.hours || 1), models: [modelName(a.modelKey)], actionKeys: [a._key],
        detail: `Done outside any system by ${staffNames(a)}: ${number(a.items || 0)} items a year. Capturing it in a system is the first step to automating it.`,
    }));
    actions.filter(a => levelOf(a) === 'assisted' && (a.items || 0) >= REPETITIVE_ITEMS && a.durationSeconds > 0 && a.durationSeconds <= REPETITIVE_SECONDS && staffTime(a) > 0)
        .forEach(a => opportunities.push({
            kind: 'automate', title: `Automate "${a.name || 'Unnamed action'}"`, hours: staffTime(a), cost: (a.cost || 0) * staffTime(a) / (a.hours || 1), models: [modelName(a.modelKey)], actionKeys: [a._key],
            detail: `A short step repeated ${number(a.items || 0)} times a year in ${list(systemsOf(a).map(s => names.get(s) || 'Unnamed'))}: a candidate for a workflow rule or an AI agent.`,
        }));
    // steps only external roles do: no staff time to save, but making them easier for those who do them is better service
    actions.filter(a => levelOf(a) === 'external' && (a.hours || 0) > 0).forEach(a => {
        const who = list((a.roles || []).map(r => names.get(r._key) || 'Unnamed role'));
        opportunities.push({
            kind: 'service', title: `Make "${a.name || 'Unnamed action'}" easier for ${who}`, hours: 0, cost: 0, externalHours: a.hours || 0,
            models: [modelName(a.modelKey)], actionKeys: [a._key],
            detail: `Done by ${who}, not staff: ${number(a.items || 0)} items a year, ${number(a.hours || 0)} hours of their time${systemsOf(a).length ? ` in ${list(systemsOf(a).map(s => names.get(s) || 'Unnamed'))}` : ''}. Making it quicker or simpler for them is better service; it saves no staff time.`,
        });
    });
    opportunities.sort((a, b) => b.hours - a.hours || (b.externalHours || 0) - (a.externalHours || 0));

    // totals
    const doneBySomeone = levels.automated + levels.assisted + levels.manual;
    const manualInterfaces = interfaceList.filter(i => i.items - i.automatedItems > 0.5);
    const infrastructureCost = infrastructure.reduce((sum, i) => sum + i.cost, 0);
    const infrastructureCarbon = infrastructure.reduce((sum, i) => sum + i.carbon, 0);
    const systemsOwnCarbon = systemNodes.reduce((sum, n) => sum + (insightsOf(n)?.emissions || []).reduce((s, e) => s + total(e), 0), 0);
    const totals = {
        systems: systems.length, aiAgents: systems.filter(s => s.aiAgent).length, infrastructure: infrastructure.length,
        cost: systems.reduce((sum, s) => sum + s.ownCost, 0) + infrastructureCost, carbon: infrastructureCarbon + systemsOwnCarbon,
        actions: levels, hours: levelHours, automatedShare: doneBySomeone ? levels.automated / doneBySomeone : null,
        staffHours: actions.reduce((sum, a) => sum + staffTime(a), 0),
        externalHours: actions.reduce((sum, a) => sum + externalHours(a, external), 0),
        interfaces: interfaceList.length, manualInterfaces: manualInterfaces.length,
        interfaceItems: interfaceList.reduce((sum, i) => sum + i.items, 0), automatedInterfaceItems: interfaceList.reduce((sum, i) => sum + i.automatedItems, 0),
        manualInterfaceHours: interfaceList.reduce((sum, i) => sum + i.manualHours, 0), manualInterfaceCost: interfaceList.reduce((sum, i) => sum + i.manualCost, 0),
        opportunityHours: [...new Set(opportunities.flatMap(o => o.actionKeys))].reduce((sum, k) => sum + staffTime(actionsByKey.get(k)!), 0),
        frictionActions: actions.filter(a => isFrictionWork(a)).length,
        frictionHours: actions.filter(a => isFrictionWork(a)).reduce((sum, a) => sum + (a.hours || 0), 0),
        frictionCost: actions.filter(a => isFrictionWork(a)).reduce((sum, a) => sum + (a.cost || 0), 0),
        frictionAutomated: actions.filter(a => isFrictionWork(a) && levelOf(a) === 'automated').length,
        valueContribution: systems.reduce((sum, s) => sum + s.contribution.value, 0),
        revenueContribution: systems.reduce((sum, s) => sum + s.contribution.revenue, 0),
        workCost: systems.reduce((sum, s) => sum + s.contribution.workCost, 0),
    };

    // what to look at first
    const findings: SystemsFinding[] = [];
    const rekeyed = interfaceList.filter(i => i.manualHours > 0 && i.fromKey !== OUTSIDE_SYSTEMS && i.toKey !== OUTSIDE_SYSTEMS);
    if (rekeyed.length) {
        const hours = rekeyed.reduce((sum, i) => sum + i.manualHours, 0);
        findings.push({
            severity: hours >= 1000 ? 'critical' : 'warning', weight: hours,
            title: `People re-key information between systems in ${plural(rekeyed.length, 'interface')}`,
            detail: `The steps that take it in by hand take ${number(hours)} hours a year and cost about ${money(rekeyed.reduce((sum, i) => sum + i.manualCost, 0))}. Not all of that is re-keying, but interfaces would remove it. The largest: ${rekeyed[0].from} → ${rekeyed[0].to}, ${number(rekeyed[0].manualHours)} hours.`,
        });
    }
    if (levelHours.manual > 0) findings.push({
        severity: 'warning', weight: levelHours.manual,
        title: `${plural(levels.manual, 'action')} ${levels.manual === 1 ? 'is' : 'are'} done outside any system`,
        detail: `${number(levelHours.manual)} hours a year of work on paper, by email or in people's heads, where it can't be tracked or automated.`,
    });
    const unused = input.scoped ? [] : systems.filter(s => !s.used && s.cost > 0);
    if (unused.length) findings.push({
        severity: 'warning', weight: unused.reduce((sum, s) => sum + s.cost, 0),
        title: `${plural(unused.length, 'system')} cost${unused.length === 1 ? 's' : ''} ${money(unused.reduce((sum, s) => sum + s.cost, 0))} a year with no modelled work`,
        detail: `${list(unused.map(s => s.name))}. Either the work that uses ${unused.length === 1 ? 'it' : 'them'} isn't modelled yet, or ${unused.length === 1 ? 'it' : 'they'} could be retired.`,
    });
    const idle = input.scoped ? [] : infrastructure.filter(i => !i.systems.some(s => s.used));
    if (idle.length) findings.push({
        severity: idle.some(i => i.cost > 0) ? 'warning' : 'info', weight: idle.reduce((sum, i) => sum + i.cost, 0),
        title: `${plural(idle.length, 'piece')} of infrastructure ${idle.length === 1 ? 'runs' : 'run'} no system that modelled work uses`,
        detail: `${list(idle.map(i => i.name))}${idle.some(i => i.cost > 0) ? `, costing ${money(idle.reduce((sum, i) => sum + i.cost, 0))} a year` : ''}. Link ${idle.length === 1 ? 'it' : 'them'} to the systems ${idle.length === 1 ? 'it runs' : 'they run'}, or consider retiring ${idle.length === 1 ? 'it' : 'them'}.`,
    });
    const inSystems = levels.automated + levels.assisted;
    systems.filter(s => inSystems && (s.automatedActions + s.assistedActions) / inSystems > DEPENDENCY_SHARE && s.models.length >= DEPENDENCY_MODELS).forEach(s => findings.push({
        severity: 'warning', weight: s.automatedActions + s.assistedActions,
        title: `Much of the work depends on ${s.name}`,
        detail: `It's involved in ${Math.round((s.automatedActions + s.assistedActions) / inSystems * 100)}% of the work done in systems, across ${plural(s.models.length, 'model')}. Check its resilience and support.`,
    }));
    const carbonSources = [...infrastructure.map(i => ({ name: i.name, carbon: i.carbon }))].filter(c => c.carbon > 0).sort((a, b) => b.carbon - a.carbon);
    if (carbonSources.length && totals.carbon > 0) findings.push({
        severity: 'info', weight: carbonSources[0].carbon,
        title: `${carbonSources[0].name} produces ${Math.round(carbonSources[0].carbon / totals.carbon * 100)}% of the carbon from systems and infrastructure`,
        detail: `${number(carbonSources[0].carbon)} kg CO2e a year of ${number(totals.carbon)} in all.`,
    });
    const noCost = systems.filter(s => s.used && s.cost === 0);
    if (noCost.length) findings.push({
        severity: 'info', weight: 0,
        title: `${plural(noCost.length, 'system')} in use ${noCost.length === 1 ? 'has' : 'have'} no cost set`,
        detail: `${list(noCost.map(s => s.name), 5)}. Set ${noCost.length === 1 ? 'its' : 'their'} costs, or the infrastructure ${noCost.length === 1 ? 'it runs' : 'they run'} on, for a full picture of what systems cost.`,
    });
    if (levels.unassigned) findings.push({
        severity: 'info', weight: 0,
        title: `${plural(levels.unassigned, 'action')} ${levels.unassigned === 1 ? 'has' : 'have'} no role or system`,
        detail: `So how ${levels.unassigned === 1 ? 'it is' : 'they are'} done isn't known. Assign a role or system to include ${levels.unassigned === 1 ? 'it' : 'them'}.`,
    });
    const order: Record<FindingSeverity, number> = { critical: 0, warning: 1, info: 2 };
    // by severity, then in the order above: re-keying, off-system work, waste, dependency, carbon, data
    findings.sort((a, b) => order[a.severity] - order[b.severity]);

    const cells = new Map<string, Map<string, number>>();
    interfaceList.forEach(i => {
        const row = cells.get(i.fromKey) || cells.set(i.fromKey, new Map()).get(i.fromKey)!;
        row.set(i.toKey, (row.get(i.toKey) || 0) + i.items);
    });

    return {
        scoped: !!input.scoped,
        systems, infrastructure, interfaces: interfaceList, outputs, opportunities, findings,
        models: [...byModel.values()].sort((a, b) => b.total - a.total),
        interfaceMatrix: matrix(cells, names, 14, 14, true),
        totals,
    };
};
